// §26.5 / §16 — the client half of Chat: four calls and no state.
//
// A separate service from `kernel/social` on purpose, and the separation is not filing. That one
// owns a connection and two timers — the doorbell and the presence heartbeat, both of which start
// at sign-in and run whether or not anybody opens an app. This one owns nothing: every method is a
// request, and there is no cache, no subscription and nothing to stop. It lives in `kernel/`
// because §6.5 keeps `apps/**` away from `fetch`, not because it holds anything.
//
// **No cache is a decision.** The obvious optimisation — hold the last page per thread and hand it
// back while revalidating — is exactly what makes a chat show a message that was deleted a minute
// ago on the other person's screen. §26.6 already gives the app a doorbell that says "re-`GET`",
// and the whole design (D-217) is that the `GET` is the single source. A cache here would be a
// second one, kept in step by hand.

import type { Message, Thread } from '@hangar/shared';

export interface ChatServiceOptions {
  /** Injected in tests; the real one in production. */
  fetch?: typeof globalThis.fetch;
}

export interface ChatService {
  threads(): Promise<Thread[]>;
  messages(peer: string, options?: { before?: string; limit?: number }): Promise<Message[]>;
  send(peer: string, body: string): Promise<Message>;
  remove(id: string): Promise<void>;
}

const BASE = '/api/chat';

/**
 * A failed request throws with the status in the message.
 *
 * The status matters to the caller here in a way it does not for the roster: §16 answers 413 for a
 * message over 4096 characters and 403 for one to somebody who is no longer a connection, and the
 * Chat app says a different sentence for each. `HttpError` from `shared` is the server's shape;
 * this is the client's, and it carries the one field that distinguishes them.
 */
export class ChatRequestError extends Error {
  readonly status: number;

  constructor(status: number, path: string) {
    super(`${path} answered ${status}`);
    this.name = 'ChatRequestError';
    this.status = status;
  }
}

export function createChatService(options: ChatServiceOptions = {}): ChatService {
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);

  const json = async <T>(path: string, init?: RequestInit): Promise<T> => {
    const response = await doFetch(`${BASE}${path}`, { credentials: 'same-origin', ...init });
    if (!response.ok) throw new ChatRequestError(response.status, `${BASE}${path}`);
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  };

  return {
    threads: async () => (await json<{ threads: Thread[] }>('/threads')).threads ?? [],

    messages: async (peer, page = {}) => {
      const query = new URLSearchParams({ peer });
      if (page.before !== undefined) query.set('before', page.before);
      if (page.limit !== undefined) query.set('limit', String(page.limit));
      return (await json<{ messages: Message[] }>(`/messages?${query.toString()}`)).messages ?? [];
    },

    send: async (peer, body) =>
      (
        await json<{ message: Message }>('/messages', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ peer, body }),
        })
      ).message,

    remove: async (id) => {
      await json<void>(`/messages/${encodeURIComponent(id)}`, { method: 'DELETE' });
    },
  };
}
